import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { CatalogSection } from "../models/CatalogSection";
import { getCatalogSections } from "../repositories/CatalogSectionRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (value: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
	showAll?: boolean;
};

const ALL_SECTION: CatalogSection = {
	SectionId: -1,
	SectionName: "All",
	IsActive: true,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
};

const useSections = (onError?: (error: Error) => void) => {
	return useQuery<CatalogSection[], Error>({
		queryKey: ["catalogSections"],
		queryFn: () => getCatalogSections(),
		onError: onError,
	});
};

export const CatalogSectionSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
	showAll = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: sections, isLoading } = useSections((error) =>
		setError(error.message),
	);

	const options = showAll ? [ALL_SECTION, ...(sections || [])] : sections || [];

	const selectedSection =
		showAll && value === null
			? ALL_SECTION
			: sections?.find((s) => s.SectionId === value) || null;

	return (
		<>
			<Autocomplete
				id="section-selector"
				fullWidth
				clearOnEscape
				options={options}
				getOptionLabel={(option) => option.SectionName}
				isOptionEqualToValue={(a, b) => a.SectionId === b.SectionId}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedSection}
				onChange={(_, newValue) => {
					onChange(
						!newValue || newValue.SectionId === -1 ? null : newValue.SectionId,
					);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading sections"
			/>
		</>
	);
};
